/**
 * @pit/sim — target selection helpers.
 *
 * Pure functions the client uses to build ordinary `setTarget` inputs.
 * The sim itself does NOT process a "tab" input — clients call the
 * helper, then send a normal setTarget with the resulting EntityId.
 * This keeps input handling exhaustive with no ambient state.
 *
 * See wp4-sim-combat.md §"Tab-cycle helper".
 */

import { distSq, imod } from "@pit/math";
import { STEALTH_AURA_DEF_ID } from "@pit/protocol";
import type { EntityId, Tick } from "@pit/protocol";
import type { Entity, SimState } from "./types.js";
import { hasActiveAuraDef } from "./auras.js";
import { isAlive } from "./stats.js";

// ─── Stealth targeting gate (#91) ────────────────────────────────────────────

/**
 * #91: is `entity` stealthed at `tick`? True iff it holds an UNEXPIRED aura
 * instance with the well-known `STEALTH_AURA_DEF_ID`.
 *
 * A pure, BUNDLE-FREE read of hashed state — an `AuraInstance` carries only its
 * def id (not its content tags), so the gate keys off the def id rather than a
 * tag (which would require a content-bundle lookup the input/targeting path
 * doesn't have). See `STEALTH_AURA_DEF_ID`. Only EXPIRY is consulted (a stealth
 * aura is not suppressible at v1, and targeting is not hashed) — the same
 * `tick < expiresAtTick` / `null ⇒ permanent` rule the aura step uses.
 *
 * #76: delegates to the generalized `hasActiveAuraDef` (auras.ts), which
 * `resolveSlotAbility`'s `holdsAura` condition also uses — the SAME
 * expiry-only, bundle-free predicate, just keyed to a different well-known id.
 */
export function isStealthed(entity: Entity, tick: Tick): boolean {
  return hasActiveAuraDef(entity, STEALTH_AURA_DEF_ID, tick);
}

/**
 * #91: can `viewer` target `target` at `tick`? The ONE targetability predicate
 * consumed by enemy target-selection (Tab-cycle / index-select) and the
 * server-authoritative `setTarget` input gate.
 *
 * FALSE only for an ENEMY viewer (different team) of a STEALTHED target — an
 * enemy genuinely cannot act on a stealthed rogue. Self + allies (same team)
 * always see it (self-target / ally-heal / friendly cycle unaffected). Pure
 * read over hashed state; adds NO hashed state itself.
 */
export function isTargetableBy(viewer: Entity, target: Entity, tick: Tick): boolean {
  if (viewer.team === target.team) return true; // self + allies unaffected
  return !isStealthed(target, tick);
}

/**
 * Next living enemy target for `seat`, cycling from the seat's current
 * `targets.hostile` in seat-index order. Returns `null` if no living
 * enemies exist.
 *
 * Deterministic: purely a function of `(state, seat)`. No module-level
 * counters or hidden state — the cycle position lives in `targets.hostile`.
 *
 * Semantics:
 *   - Enemies are entities on a different team from the seat.
 *   - Living enemies are entities with `hp > 0`.
 *   - If `targets.hostile` is currently set to a living enemy, the next
 *     cycle picks the NEXT living enemy after it (seat order, wrapping).
 *   - If `targets.hostile` is null OR points at a dead entity, the cycle
 *     picks the FIRST living enemy in seat order.
 *   - If the current target is the only living enemy, returns the same id
 *     (so re-tab keeps the target on rather than dropping it).
 *   - Callers are expected to send the returned id via `setTarget`; the
 *     sim never mutates state as a side effect of this helper.
 */
export function nextEnemyTarget(
  state: SimState,
  seat: number,
): EntityId | null {
  const actor = state.entities[seat];
  if (actor === undefined) return null;

  // Collect living, TARGETABLE enemies in seat order. #91: a stealthed enemy is
  // skipped (untargetable by an enemy viewer), so Tab-cycle can never land on
  // it — the same enforcement the server-side setTarget gate applies.
  const enemies: Entity[] = [];
  for (const e of state.entities) {
    if (e.team === actor.team) continue;
    if (!isAlive(e)) continue;
    if (!isTargetableBy(actor, e, state.tick)) continue;
    enemies.push(e);
  }
  if (enemies.length === 0) return null;

  const current = actor.targets.hostile;
  if (current === null) {
    // No current target — start from seat order.
    const first = enemies[0];
    if (first === undefined) return null;
    return first.id;
  }

  // Find the current target's index within the living-enemies list. If
  // absent (target died or is friendly), start from the beginning.
  let idx = -1;
  for (let i = 0; i < enemies.length; i++) {
    const e = enemies[i];
    if (e === undefined) continue;
    if (e.id === current) {
      idx = i;
      break;
    }
  }
  if (idx < 0) {
    const first = enemies[0];
    if (first === undefined) return null;
    return first.id;
  }
  // Cycle to the next living enemy in seat order.
  const nextIdx = imod(idx + 1, enemies.length);
  const next = enemies[nextIdx];
  if (next === undefined) return null;
  return next.id;
}

/**
 * #815: the NEAREST living, targetable enemy of `seat` — `null` when none
 * exists. The distance-ordered sibling of `nextEnemyTarget`'s seat-order cycle.
 *
 * WHY IT LIVES HERE (and not in the client): target semantics — who counts as
 * an enemy, who is alive, who is targetable — are owned by the sim, and the
 * client's `targetSelectWire.ts` exists precisely so there is never a second
 * copy of those rules. Two client entry points consume this (Tab, and
 * auto-acquire on an `enemy`-targetType cast), so writing it once here is what
 * keeps them from disagreeing about what "nearest" means.
 *
 * NOT A TICK-LOOP HELPER: like every other function in this module it is a pure
 * READ over state — nothing in `step.ts` calls it, it adds no hashed state, and
 * it therefore changes no golden and needs no `SIM_VERSION` bump. The sim never
 * auto-targets; a client sends an ordinary `setTarget` with the result.
 *
 * Determinism:
 *   - Integer-only: `distSq` (squared Euclidean, `@pit/math`) — no `Math.sqrt`,
 *     no floats, so comparisons are exact.
 *   - Canonical iteration: `state.entities` in SEAT order (seat === index),
 *     the same order `nextEnemyTarget` walks.
 *   - TIE-BREAK: strictly-less-than, so the LOWEST SEAT wins an exact distance
 *     tie. A tie must resolve the same way every call or the client would
 *     flicker between two equidistant targets.
 *
 * Skips exactly what `nextEnemyTarget` skips: same-team entities, corpses
 * (`isAlive`), and #91-stealthed enemies (`isTargetableBy`).
 */
export function nearestEnemyTarget(state: SimState, seat: number): EntityId | null {
  const actor = state.entities[seat];
  if (actor === undefined) return null;

  let best: Entity | null = null;
  let bestDistSq = 0;
  for (const e of state.entities) {
    if (e.team === actor.team) continue;
    if (!isAlive(e)) continue;
    if (!isTargetableBy(actor, e, state.tick)) continue;
    const d2 = distSq(actor.x, actor.y, e.x, e.y);
    // Strict `<` keeps the FIRST (lowest-seat) entity on a tie — see above.
    if (best === null || d2 < bestDistSq) {
      best = e;
      bestDistSq = d2;
    }
  }
  return best === null ? null : best.id;
}

/**
 * #815: does `seat`'s HOSTILE target slot still hold a usable enemy? True iff
 * `targets.hostile` names an entity that (a) exists, (b) is on a different
 * team, (c) is alive, and (d) is targetable by the actor (#91 stealth).
 *
 * The "do I need to acquire a target?" predicate behind the client's
 * auto-acquire: FALSE is exactly the "no target, or the current one is
 * dead/invalid/no-longer-targetable" condition. Kept here, next to
 * `nearestEnemyTarget`, so validity and selection can never drift apart —
 * both read the same `isAlive`/`isTargetableBy` pair the sim's own
 * `setTarget` gate and invariant I-stealth use.
 *
 * Pure read; no hashed state, no tick-loop caller (see `nearestEnemyTarget`).
 */
export function hasLiveEnemyTarget(state: SimState, seat: number): boolean {
  const actor = state.entities[seat];
  if (actor === undefined) return false;
  const current = actor.targets.hostile;
  if (current === null) return false;
  for (const e of state.entities) {
    if (e.id !== current) continue;
    if (e.team === actor.team) return false; // friendly id in the hostile slot
    if (!isAlive(e)) return false; // corpse
    return isTargetableBy(actor, e, state.tick); // #91 stealth
  }
  return false; // stale id — the entity is gone
}

// ─── Deterministic rosters (#63) ─────────────────────────────────────────────
//
// Arena/party direct-select hotkeys map an index N to a STABLE entity across
// ticks. To keep that mapping stable we build the roster over ALL matching
// entities (dead included) in a canonical order, then check liveness at the
// selection site. If we filtered out corpses first, an ally/enemy dying would
// renumber every later index — so `arena2` would silently point at a different
// unit. Roster slots are fixed; a dead entity at a slot is a no-op (returns
// null), never a shift.
//
// ORDER (documented, deterministic, tie-broken by seat — seats are unique so
// the order is total):
//   • Enemy roster  — every entity on a DIFFERENT team from the actor, in
//     ascending seat order.
//   • Ally roster   — the actor FIRST (WoW party-frame convention: party1 =
//     self), then every OTHER same-team entity in ascending seat order.

/** Enemies of `actor` (different team), ascending seat order, dead included. */
function enemyRoster(state: SimState, actor: Entity): Entity[] {
  const roster: Entity[] = [];
  for (const e of state.entities) {
    if (e.team === actor.team) continue;
    roster.push(e);
  }
  // `state.entities` is already seat-indexed (seat === array index), so it is
  // ascending-seat by construction; no sort needed. Kept explicit for intent.
  return roster;
}

/** Allies of `actor` (same team), self FIRST then ascending seat, dead included. */
function allyRoster(state: SimState, actor: Entity): Entity[] {
  const others: Entity[] = [];
  for (const e of state.entities) {
    if (e.team !== actor.team) continue;
    if (e.seat === actor.seat) continue; // self is pinned first below
    others.push(e);
  }
  return [actor, ...others];
}

/**
 * Next living ally target for `seat`, cycling from the seat's current
 * `targets.friendly` in ALLY-ROSTER order (self first, then other allies in
 * seat order). Returns `null` if no living allies exist.
 *
 * The friendly-slot mirror of `nextEnemyTarget` — same stateless discipline:
 * the cycle position lives entirely in `targets.friendly`, no hidden cursor,
 * so it is a pure function of `(state, seat)`.
 *
 * Semantics (mirror of `nextEnemyTarget`, inverted faction):
 *   - Allies are entities on the SAME team as the seat, INCLUDING the actor
 *     itself (self is a valid friendly target — self-heals land here).
 *   - Living allies are entities with `hp > 0`.
 *   - If `targets.friendly` is currently a living ally, the next cycle picks
 *     the NEXT living ally after it (roster order, wrapping).
 *   - If `targets.friendly` is null OR points at a dead/unknown entity, the
 *     cycle picks the FIRST living ally in roster order (self, if living).
 *   - If the current target is the only living ally, returns the same id.
 *   - Callers send the returned id via `setTarget`; the sim auto-routes it to
 *     the friendly slot by faction.
 */
export function nextAllyTarget(
  state: SimState,
  seat: number,
): EntityId | null {
  const actor = state.entities[seat];
  if (actor === undefined) return null;

  // Living allies in roster order (self first).
  const allies: Entity[] = [];
  for (const e of allyRoster(state, actor)) {
    if (!isAlive(e)) continue;
    allies.push(e);
  }
  if (allies.length === 0) return null;

  const current = actor.targets.friendly;
  if (current === null) {
    const first = allies[0];
    if (first === undefined) return null;
    return first.id;
  }

  let idx = -1;
  for (let i = 0; i < allies.length; i++) {
    const e = allies[i];
    if (e === undefined) continue;
    if (e.id === current) {
      idx = i;
      break;
    }
  }
  if (idx < 0) {
    const first = allies[0];
    if (first === undefined) return null;
    return first.id;
  }
  const nextIdx = imod(idx + 1, allies.length);
  const next = allies[nextIdx];
  if (next === undefined) return null;
  return next.id;
}

/**
 * The `index`-th ENEMY of `seat` (arena hotkey `arenaN` → index `N-1`), by the
 * stable enemy roster (different team, ascending seat, dead included). Feeds
 * the HOSTILE slot via `setTarget` (faction auto-routes).
 *
 * Direct index select — NOT a cycle: `enemyByIndex(_, seat, 1)` always maps to
 * the same roster slot regardless of the current target.
 *
 * Returns `null` (no-op — keep current target) when:
 *   - `index` is out of range (`< 0` or `>= roster length`) — a hotkey for an
 *     empty seat is a harmless no-op; and
 *   - the roster slot holds a DEAD entity — a corpse at a direct index does not
 *     clear or change the current target (design decision: no-op, not clear).
 */
export function enemyByIndex(
  state: SimState,
  seat: number,
  index: number,
): EntityId | null {
  const actor = state.entities[seat];
  if (actor === undefined) return null;
  const roster = enemyRoster(state, actor);
  const target = roster[index];
  if (target === undefined) return null; // out of range
  if (!isAlive(target)) return null; // corpse at slot — no-op
  // #91: a stealthed enemy at the roster slot is untargetable by an enemy
  // viewer — a no-op (keep current target), same as a corpse at the slot.
  if (!isTargetableBy(actor, target, state.tick)) return null;
  return target.id;
}

/**
 * The `index`-th ALLY of `seat` (party hotkey `partyN` → index `N-1`), by the
 * stable ally roster (self first, then other same-team entities in ascending
 * seat order, dead included). Feeds the FRIENDLY slot via `setTarget` (faction
 * auto-routes). `index === 0` is always SELF (WoW party-frame convention).
 *
 * Direct index select — NOT a cycle: `allyByIndex(_, seat, 0)` always targets
 * self, `allyByIndex(_, seat, 1)` always the first other ally, etc.
 *
 * Returns `null` (no-op — keep current target) on the same conditions as
 * `enemyByIndex`: `index` out of range, or a dead entity at the roster slot.
 */
export function allyByIndex(
  state: SimState,
  seat: number,
  index: number,
): EntityId | null {
  const actor = state.entities[seat];
  if (actor === undefined) return null;
  const roster = allyRoster(state, actor);
  const target = roster[index];
  if (target === undefined) return null; // out of range
  if (!isAlive(target)) return null; // corpse at slot — no-op
  return target.id;
}
